import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { setSEO } from '../utils/seo';
import { reservationApi } from '../api/reservationApi';
import { Reservation } from '../types';
import { formatDateTimeDisplay, formatPrice } from '../utils/dateUtils';
import { useAuthStore } from '../stores/authStore';
import { localizeVehicleModel } from '../utils/localization';

type PeriodFilter = '30days' | '90days' | '180days' | 'custom';

const MyReservationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuthStore();
  const [userReservations, setUserReservations] = useState<Reservation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    setSEO('checkReservation');

    // 로그인하지 않은 경우 홈으로 리다이렉트
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    // 로그인한 사용자의 예약 목록 불러오기 (JWT 토큰으로 자동 인증)
    loadUserReservations();
  }, [isAuthenticated, navigate, periodFilter, customStartDate, customEndDate]);

  const loadUserReservations = async () => {
    setIsLoading(true);
    try {
      let params: { days?: number; start_date?: string; end_date?: string } = {};

      if (periodFilter === 'custom' && customStartDate && customEndDate) {
        params = {
          start_date: customStartDate,
          end_date: customEndDate,
        };
      } else if (periodFilter === '30days') {
        params = { days: 30 };
      } else if (periodFilter === '90days') {
        params = { days: 90 };
      } else if (periodFilter === '180days') {
        params = { days: 180 };
      }

      const reservations = await reservationApi.getUserReservations(params);
      setUserReservations(reservations);
    } catch (error) {
      console.error('Failed to load user reservations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePeriodFilterChange = (filter: PeriodFilter) => {
    setPeriodFilter(filter);
    if (filter !== 'custom') {
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const formatReservationDate = (date: Date) => {
    return i18n.language.startsWith('en')
      ? date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : formatDateTimeDisplay(date);
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('myReservations.title')}</h1>
            <p className="text-gray-600">{t('myReservations.subtitle')}</p>
          </div>

          {/* Period Filter */}
          <div className="bg-white rounded-lg shadow-sm mb-6 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('myReservations.period')}</h3>

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => handlePeriodFilterChange('30days')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  periodFilter === '30days'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('myReservations.last30')}
              </button>
              <button
                onClick={() => handlePeriodFilterChange('90days')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  periodFilter === '90days'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('myReservations.last90')}
              </button>
              <button
                onClick={() => handlePeriodFilterChange('180days')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  periodFilter === '180days'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('myReservations.last180')}
              </button>
              <button
                onClick={() => handlePeriodFilterChange('custom')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  periodFilter === 'custom'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('myReservations.custom')}
              </button>
            </div>

            {/* Custom Date Picker */}
            {periodFilter === 'custom' && (
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-xs text-gray-600 mb-1">{t('myReservations.startDate')}</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-xs text-gray-600 mb-1">{t('myReservations.endDate')}</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  onClick={() => loadUserReservations()}
                  disabled={!customStartDate || !customEndDate}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {t('myReservations.search')}
                </button>
              </div>
            )}
          </div>

          {/* User's Reservations */}
          {isLoading ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">{t('myReservations.loading')}</p>
            </div>
          ) : userReservations.length > 0 ? (
            <div className="space-y-4">
              {userReservations.map((reservation) => (
                <div
                  key={reservation.id}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => {
                    if (reservation.reservation_number && reservation.user_name) {
                      // Base64 인코딩된 토큰 생성 (예약번호:이름)
                      const tokenData = `${reservation.reservation_number}:${reservation.user_name}`;
                      const token = btoa(unescape(encodeURIComponent(tokenData)));
                      navigate(`/reservation/detail?token=${encodeURIComponent(token)}`);
                    }
                  }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {localizeVehicleModel(reservation.vehicle_name, i18n.language)}
                      </h3>
                      <p className="text-sm text-gray-500">{t('myReservations.reservationNumber')}: {reservation.reservation_number}</p>
                      {reservation.vehicle_number && (
                        <p className="text-sm text-gray-500">{t('myReservations.vehicleNumber')}: {reservation.vehicle_number}</p>
                      )}
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        reservation.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : reservation.status === 'in_use'
                          ? 'bg-blue-100 text-blue-800'
                          : reservation.status === 'completed'
                          ? 'bg-gray-100 text-gray-800'
                          : reservation.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : reservation.status === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {reservation.status === 'confirmed'
                        ? t('myReservations.confirmed')
                        : reservation.status === 'in_use'
                        ? t('myReservations.inUse')
                        : reservation.status === 'completed'
                        ? t('myReservations.completed')
                        : reservation.status === 'pending'
                        ? t('myReservations.pending')
                        : reservation.status === 'cancelled'
                        ? t('myReservations.cancelled')
                        : reservation.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">{t('myReservations.startDateTime')}</p>
                      <p className="font-semibold text-gray-900">
                        {formatReservationDate(new Date(reservation.start_date))}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">{t('myReservations.endDateTime')}</p>
                      <p className="font-semibold text-gray-900">
                        {formatReservationDate(new Date(reservation.end_date))}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('myReservations.totalPayment')}</span>
                      <span className="text-xl font-bold text-blue-600">
                        {formatPrice(reservation.total_amount)}
                      </span>
                    </div>
                  </div>

                  {/* 버튼 영역 (이용 완료 상태에만 표시) */}
                  {reservation.status === 'completed' && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex gap-3">
                        {/* 예약상세 보기 버튼 */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // 예약 상세로 이동하지 않고 현재 카드 자체가 클릭 가능하므로 상세 페이지로 이동
                            if (reservation.reservation_number && reservation.user_name) {
                              const tokenData = `${reservation.reservation_number}:${reservation.user_name}`;
                              const token = btoa(unescape(encodeURIComponent(tokenData)));
                              navigate(`/reservation/detail?token=${encodeURIComponent(token)}`);
                            }
                          }}
                          className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-all font-semibold flex items-center justify-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {t('myReservations.detail')}
                        </button>

                        {/* 리뷰 버튼 */}
                        {!reservation.has_review ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Base64 인코딩된 토큰 생성 (예약번호:이름)
                              const tokenData = `${reservation.reservation_number}:${reservation.user_name}`;
                              const token = btoa(unescape(encodeURIComponent(tokenData)));
                              navigate(`/review/${encodeURIComponent(token)}`);
                            }}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-300 to-blue-400 text-white rounded-lg hover:from-blue-400 hover:to-blue-500 transition-all font-semibold flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                            {t('myReservations.writeReview')}
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Base64 인코딩된 토큰 생성 (예약번호:이름)
                              const tokenData = `${reservation.reservation_number}:${reservation.user_name}`;
                              const token = btoa(unescape(encodeURIComponent(tokenData)));
                              navigate(`/review/${encodeURIComponent(token)}`);
                            }}
                            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-semibold flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                            {t('myReservations.viewReview')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-gray-600">{t('myReservations.empty')}</p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MyReservationsPage;
